import useEmblaCarousel from 'embla-carousel-react';
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useIsMounted,
  useOnClickOutside,
} from 'usehooks-ts';

import { getCookieCurrency } from '@/features/currency/cookies/cookie-currency';
import ListingCard from '@/features/listing/components/listing-card/listing-card';
import {
  NextButton,
  PrevButton,
  useCarouselArrows,
} from '@/features/listing/components/listing-card/listing-carousel-arrows/use-carousel-arrows';
import getMapPreviewListings from '@/features/listing/requests/get-map-preview-listings';
import {
  type ListingWithUser,
  type PreviewListingsData,
} from '@/features/listing/types/listing-types';
import Skeleton from '@/ui/skeleton/skeleton';

import styles from './listings-map-preview.module.scss';

interface ListingsMapPreviewProps {
  previewListingsData: PreviewListingsData;
  onClose: () => void;
}

export default function ListingsMapPreview(props: ListingsMapPreviewProps) {
  const {
    onClose,
    previewListingsData,
  } = props;

  const isMounted = useIsMounted();

  const [isListingsLoading,
    setIsListingsLoading] = useState(true);
  const [listings,
    setListings] = useState<ListingWithUser[]>([]);
  const [listingsCurrentPage,
    setListingsCurrentPage] = useState(0);

  const listingsMapPreviewRef = useRef(null);
  const listingsFetchLimit = 5;
  const totalPages = Math.ceil(previewListingsData.listingsCount / listingsFetchLimit);

  useOnClickOutside(listingsMapPreviewRef, onClose);

  const [emblaRef,
    emblaApi] = useEmblaCarousel({ align: 'start' });

  const {
    nextBtnDisabled,
    onNextButtonClick,
    onPrevButtonClick,
    prevBtnDisabled,
  } = useCarouselArrows(emblaApi);

  const fetchMapPreviewListings = useCallback((pageNumber: number) => {
    const currentCurrency = getCookieCurrency();

    if (previewListingsData.listingsSource) {
      const listingsToSkip = pageNumber * listingsFetchLimit;

      previewListingsData.listingsSource
        .getClusterLeaves(previewListingsData.clusterId, listingsFetchLimit, listingsToSkip)
        .then((mapListingsFeaturesData) => {
          const listingsIds: number[] = [];

          mapListingsFeaturesData.forEach((listingFeature) => {
            if (listingFeature.properties) {
              listingsIds.push(listingFeature.properties.listingId as number);
            }
          });
          setListingsCurrentPage(listingsCurrentPage + 1);

          // const listingsIdsToFetch = listingsIds.length > 10 ? listingsIds.slice(0, 10) : listingsIds;

          getMapPreviewListings(listingsIds, currentCurrency, true)
            .then((listingsData) => {
              setListings([...listings, ...listingsData]);
            })
            .catch(() => null)
            .finally(() => { setIsListingsLoading(false); });
        })
        .catch(() => null);
    } else {
      getMapPreviewListings([previewListingsData.listingId], currentCurrency)
        .then((listingsData) => {
          setListings([...listings, ...listingsData]);
        })
        .catch(() => null)
        .finally(() => { setIsListingsLoading(false); });
    }
  }, [listings, listingsCurrentPage, previewListingsData.clusterId, previewListingsData.listingId, previewListingsData.listingsSource]);

  const handleNextButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (previewListingsData.listingsSource && listingsCurrentPage < totalPages) {
      fetchMapPreviewListings(listingsCurrentPage);
    }

    onNextButtonClick(event);
  };

  useEffect(() => {
    fetchMapPreviewListings(listingsCurrentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  return (
    <div ref={listingsMapPreviewRef} className={styles.mapListingsPreview}>
      <div className={styles.embla}>
        <div className={styles.embla__viewport} ref={emblaRef}>
          <div className={styles.embla__container}>
            {isListingsLoading && (
              <Skeleton />
            )}

            {!isListingsLoading && listings.length > 0 && listings.map((listing) => (
              <div key={listing.id} className={styles.embla__slide}>
                <ListingCard
                  listing={listing}
                  showMessageButton={false}
                  showVerifications={false}
                  showDescription={false}
                  showMoveInDate={false}
                  hideBorder
                />
              </div>
            ))}
          </div>
        </div>

        {listings.length > 1 && (
          <>
            <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} isMapPreview />
            <NextButton onClick={handleNextButtonClick} disabled={nextBtnDisabled} isMapPreview />
          </>
        )}
      </div>
    </div>
  );
}
